import { isEmptyString, isNil } from './object';
import { toHexString } from './array';
import { getMediaTypeForMimeType } from './mediaUtils';
import { MEDIA_TYPE } from '../constants';
import { getAxiosInstance } from '../api/worker/AxiosWrapper';
import MediaLoader from '../features/media/loader/MediaLoader';
import MediaMetadataError from '../errors/MediaMetadataError';
import MediaFormatError from '../errors/MediaFormatError';

export function createObjectURL(object) {
  return self.URL.createObjectURL(object);
}

export function revokeObjectURL(url) {
  return self.URL.revokeObjectURL(url);
}

/**
 * Loads a file from the users local system as an array buffer
 * @param file
 * @returns {Promise<any>}
 */
export function loadFile(file) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = (e) => {
      const resultArray = new Uint8Array(e.target.result);
      resolve(resultArray);
    };

    fileReader.onerror = (e) => {
      reject(e);
    };

    fileReader.readAsArrayBuffer(file);
  });
}

/**
 * Check for images with incorrect file extensions by reading header bytes
 * Should prevent unsupported image types to be uploaded.
 * magic numbering info here: https://en.wikipedia.org/wiki/Magic_number_%28programming%29
 */
export function getImageTypeFromFileHeader(file) {
  return new Promise((resolve, reject) => {
    try {
      const fileReader = new FileReader();
      fileReader.onloadend = (e) => {
        let imageType = undefined;
        const arr = new Uint8Array(e.target.result).subarray(0, 4);
        const header = toHexString(arr);

        // Check the file signature against known types
        if (header.startsWith('ffd8')) {
          imageType = 'image/jpeg';
        } else if (header.startsWith('89504e47')) {
          imageType = 'image/png';
        } else if (header.startsWith('47494638')) {
          imageType = 'image/gif';
        }
        resolve({ header, imageType });
      };
      fileReader.readAsArrayBuffer(file);
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Gets a data URL from a File
 * @param file
 * @returns {Promise<never>|Promise<any>}
 */
function getDataURIFromFile(file) {
  if (isNil(file)) return Promise.reject(new Error('File is invalid'));

  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        resolve(reader.result);
      });

      reader.addEventListener('error', (error) => {
        reject(error);
      });

      reader.readAsDataURL(file);
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Gets a local URL (data or blob) from a file.
 * If the type if Video, we try to load the file and if succesfull we use the blob URL.
 * Otherwise we use the data url.
 *
 * @param file
 * @returns {Promise<never>|Promise<any>}
 */
export function getLocalURIFromFile(file) {
  if (isNil(file)) return Promise.reject(new Error('File is invalid'));

  return new Promise((resolve, reject) => {
    const mediaType = getMediaTypeForMimeType(file.type);

    if (mediaType === MEDIA_TYPE.VIDEO) {
      // Try loading as a video file in a <video> tag.
      const videoBlobUrl = createObjectURL(file);

      // Do not use media loader factory here.
      const mediaLoader = new MediaLoader(videoBlobUrl, mediaType);
      mediaLoader.maxTries = 1;
      mediaLoader
        .loadMetaData()
        .then(() => {
          resolve(videoBlobUrl);
        })
        .catch((error) => {
          revokeObjectURL(videoBlobUrl);

          if (
            error instanceof MediaMetadataError ||
            error instanceof MediaFormatError
          ) {
            reject(error);
            return;
          }

          resolve(getDataURIFromFile(file));
        });
      return;
    }

    if (mediaType === MEDIA_TYPE.IMAGE) {
      // Try looking at file header for known image types
      getImageTypeFromFileHeader(file).then(({ imageType }) => {
        if (isEmptyString(imageType)) {
          resolve(getDataURIFromFile(file));
        } else {
          resolve(createObjectURL(file));
        }
      });
      return;
    }

    resolve(getDataURIFromFile(file));
  });
}

/**
 * Gets an array buffer from a data URL
 * @param dataURI
 * @returns {Promise<never>|Promise<ArrayBuffer>}
 */
function getArrayBufferFromDataURI(dataURI) {
  if (isEmptyString(dataURI)) {
    return Promise.reject(new Error('File is invalid'));
  }

  try {
    const byteString = atob(dataURI.split(',')[1]);

    // write the bytes of the string to an ArrayBuffer
    const arrayBuffer = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    const uint8Array = new Uint8Array(arrayBuffer);

    // set the bytes of the buffer to the correct values
    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    return Promise.resolve(arrayBuffer);
  } catch (error) {
    return Promise.reject(error);
  }
}

/**
 * Gets an array buffer from a blob URL using an XHTTP request.
 * @param blobURI
 * @returns {Promise<never>|Promise<any>}
 */
function getArrayBufferFromBlobURI(blobURI) {
  if (isEmptyString(blobURI)) {
    return Promise.reject(new Error('File is invalid'));
  }

  return new Promise((resolve, reject) => {
    try {
      const axios = getAxiosInstance();
      axios({
        url: blobURI,
        method: 'get',
        responseType: 'arraybuffer',
      })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Gets an array buffer from a local data or blob url.
 * @param fileURI
 * @returns {Promise<never>|Promise<never>|Promise<ArrayBuffer>|Promise<never>|Promise<any>}
 */
export function getArrayBufferFromFileURI(fileURI) {
  if (isEmptyString(fileURI)) {
    return Promise.reject(new Error('File is invalid'));
  }

  try {
    if (!fileURI.startsWith('data:') && !fileURI.startsWith('blob:')) {
      return Promise.reject(new Error('Cannot decode file URI'));
    }

    if (fileURI.startsWith('blob:')) {
      return getArrayBufferFromBlobURI(fileURI);
    }

    return getArrayBufferFromDataURI(fileURI);
  } catch (err) {
    return Promise.reject(err);
  }
}

export const supportsCreateImageBitmap = () => {
  return 'createImageBitmap' in self;
};

/*
 * Safari and Edge polyfill for createImageBitmap
 * https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/createImageBitmap
 *
 * Support source image types Blob and ImageData.
 * Based on: https://gist.github.com/MonsieurV/fb640c29084c171b4444184858a91bc7
 * Based on: https://gist.github.com/Nektro/84654b5183ddd1ccb7489607239c982d
 */
export const createImageBitmap = (data) => {
  return new Promise((resolve, reject) => {
    if (supportsCreateImageBitmap()) {
      self.createImageBitmap(data).then((imageBitmap) => {
        resolve(imageBitmap);
      });
    } else {
      const img = document.createElement('img');
      if (data instanceof Blob) {
        getLocalURIFromFile(data)
          .then((url) => {
            img.src = url;
          })
          .catch((error) => {
            reject(error);
          });
      } else if (data instanceof ImageData) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = data.width;
        canvas.height = data.height;
        ctx.putImageData(data, 0, 0);
        img.src = canvas.toDataURL();
      } else if (data instanceof HTMLImageElement) {
        resolve(data);
      } else {
        reject(
          new Error(
            'createImageBitmap does not handle the provided image source type',
          ),
        );
      }

      img.addEventListener('load', () => {
        resolve(this);
      });

      img.addEventListener('error', (error) => {
        reject(error);
      });
    }
  });
};
